import React, { useState, useCallback } from "react";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import { FeatherEdit } from "@subframe/core";
import { FeatherSave } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { FeatherChevronDown } from "@subframe/core";
import { FeatherInfo } from "@subframe/core";
import * as SubframeCore from "@subframe/core";
import ReviewChangesModal, { ChangeItem } from "./ReviewChangesModal";
import { SearchableDropdown } from "../../components";
import type { TcsEntity } from "../../../domain/entities/TaxEntity";
import type { TcsRecommendationEntity } from "../../../domain/entities/TcsRecommendationEntity";
import { fetchTcsList, mapTcsDTOToEntity } from "../../../data/api/tcsApi";
import { truncateText } from "../../../utils/formatters";
import { ReviewPendingInlineValue } from "./ReviewFieldDiffHint";

export type AmountSummaryFieldDiffSlice = {
  oldText: string;
  newText: string;
};

interface AmountSummaryData {
  subtotal: string;
  gstValue: string;
  discountsAdjustments: string;
  totalAmount: string;
}

interface AmountSummaryOptionsData {
  subtotal: string[];
  gstValue: string[];
  discountsAdjustments: string[];
  totalAmount: string[];
}

export type TcsSelection = {
  tcs_tax_id: string;
  tcs_tax_name: string;
  tcs_tax_percentage: number;
  tcs_nature_of_collection?: string;
} | null;

interface AmountSummaryEditableFieldProps {
  initialData: AmountSummaryData;
  isGstEnabled: boolean;
  optionsData?: AmountSummaryOptionsData;
  onSave?: (newData: AmountSummaryData) => void;
  lineItems?: any[];
  currencySymbol?: string;
  currentDiscount?: number;
  onDiscountChange?: (discount: number) => void;
  disabled?: boolean;
  /** TCS (Tax Collected at Source) - shown only when isGstEnabled */
  tcsTaxId?: string | null;
  tcsTaxName?: string | null;
  tcsTaxPercentage?: number;
  onTcsChange?: (value: TcsSelection) => void;
  /** When true (e.g. TDS applied on a line item), TCS dropdown is disabled and message is shown */
  tcsDisabled?: boolean;
  tcsDisabledMessage?: string;
  /** AI TCS recommendation for popover; show info icon only when present and user has not changed TCS */
  tcsRecommendation?: TcsRecommendationEntity | null;
  /** When true, hide the TCS recommendation info icon (user changed TCS from dropdown) */
  userHasChangedTcs?: boolean;
  /** FBAC: maps JSON header keys to display deltas */
  amountFieldDiffs?: Partial<
    Record<
      | "sub_total"
      | "tax_total"
      | "total"
      | "tcs_tax_name"
      | "tcs_tax_percentage",
      AmountSummaryFieldDiffSlice
    >
  >;
  pendingReviewDiffActive?: boolean;
}

const NO_TCS_ENTITY: TcsEntity = {
  id: 0,
  tcs_id: "",
  tcs_tax_name: "No TCS",
  tcs_tax_percentage: 0,
};

const AmountSummaryEditableField: React.FC<AmountSummaryEditableFieldProps> = ({
  initialData = {
    subtotal: "",
    gstValue: "",
    discountsAdjustments: "",
    totalAmount: "",
  },
  optionsData = {
    subtotal: [],
    gstValue: [],
    discountsAdjustments: [],
    totalAmount: [],
  },
  onSave,
  isGstEnabled,
  lineItems = [],
  currencySymbol = "₹",
  currentDiscount = 0,
  onDiscountChange,
  disabled = false,
  tcsTaxId = null,
  tcsTaxName = null,
  tcsTaxPercentage,
  onTcsChange,
  tcsDisabled = false,
  tcsDisabledMessage = "TDS is applied. TCS cannot be selected when TDS is applied on a line item.",
  tcsRecommendation = null,
  userHasChangedTcs = false,
  amountFieldDiffs,
  pendingReviewDiffActive = false,
}) => {
  // Calculate raw subtotal from line items (rate * quantity)
  const rawSubtotal: number = lineItems.reduce(
    (sum, item) => sum + (item.rate || 0) * (item.quantity || 0),
    0,
  );

  // Calculate GST from line items
  const calculatedGst = lineItems.reduce((sum, item) => {
    const taxPercentage = item.tax_percentage || 0;
    const taxAmount =
      (item.rate || 0) * (item.quantity || 0) * (taxPercentage / 100);
    return sum + taxAmount;
  }, 0);

  const [isEditing, setIsEditing] = useState(false);
  const [data, setData] = useState<AmountSummaryData>(initialData);
  const [editData, setEditData] = useState<AmountSummaryData>(initialData);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<ChangeItem[]>([]);
  const [discountInputValue, setDiscountInputValue] = useState<string>(
    currentDiscount.toFixed(2),
  );
  /** TCS snapshot when user entered edit mode (to show in review modal) */
  const [initialTcsSnapshot, setInitialTcsSnapshot] = useState<{
    id: string;
    name: string;
    pct: number | undefined;
  } | null>(null);
  /** Pending TCS selection during edit (only applied on confirm). undefined = use props. */
  const [pendingTcsSelection, setPendingTcsSelection] = useState<
    TcsSelection | undefined
  >(undefined);
  /** TCS value to pass to onTcsChange when user confirms in modal (set when modal opens). */
  const [tcsValueToSaveOnConfirm, setTcsValueToSaveOnConfirm] = useState<
    TcsSelection | undefined
  >(undefined);

  // Sync discount input when currentDiscount changes from outside
  React.useEffect(() => {
    if (!isEditing) {
      setDiscountInputValue(currentDiscount.toFixed(2));
    }
  }, [currentDiscount, isEditing]);

  // During edit: show and calculate with pending TCS; otherwise use props (only applied on confirm)
  const effectiveTcsId =
    isEditing && pendingTcsSelection !== undefined
      ? (pendingTcsSelection?.tcs_tax_id ?? "")
      : (tcsTaxId ?? "");
  const effectiveTcsName =
    isEditing && pendingTcsSelection !== undefined
      ? (pendingTcsSelection?.tcs_tax_name ?? "No TCS")
      : (tcsTaxName ?? "No TCS");
  const effectiveTcsPct =
    isEditing && pendingTcsSelection !== undefined
      ? pendingTcsSelection?.tcs_tax_percentage
      : tcsTaxPercentage;

  const handleEdit = () => {
    setEditData(data);
    setDiscountInputValue(currentDiscount.toFixed(2));
    setInitialTcsSnapshot(
      isGstEnabled
        ? {
            id: tcsTaxId ?? "",
            name: tcsTaxName ?? "No TCS",
            pct: tcsTaxPercentage,
          }
        : null,
    );
    setPendingTcsSelection(undefined);
    setTcsValueToSaveOnConfirm(undefined);
    setIsEditing(true);
  };

  const handleSave = () => {
    // Check for changes and create change items
    const changes: ChangeItem[] = [];

    Object.keys(editData).forEach((key) => {
      const fieldKey = key as keyof AmountSummaryData;
      if (data[fieldKey] !== editData[fieldKey]) {
        changes.push({
          field:
            key.charAt(0).toUpperCase() +
            key.slice(1).replace(/([A-Z])/g, " $1"),
          previousValue: data[fieldKey],
          newValue: editData[fieldKey],
        });
      }
    });

    // Check if discount changed
    const editingDiscountValue = parseFloat(discountInputValue) || 0;
    if (Math.abs(editingDiscountValue - currentDiscount) > 0.001) {
      changes.push({
        field: "Discount/Adjustments",
        previousValue: `${currencySymbol}${currentDiscount.toFixed(2)}`,
        newValue: `${currencySymbol}${editingDiscountValue.toFixed(2)}`,
      });
    }

    // Check if TCS changed (only when GST enabled and we have a snapshot from edit start; use effective TCS from component scope)
    if (isGstEnabled && initialTcsSnapshot) {
      const prevDisplay =
        !initialTcsSnapshot.id || !initialTcsSnapshot.name
          ? "No TCS"
          : initialTcsSnapshot.pct != null
            ? `${initialTcsSnapshot.name}`
            : initialTcsSnapshot.name;
      const currDisplay =
        !effectiveTcsId || !effectiveTcsName ? "No TCS" : effectiveTcsName;
      if (
        initialTcsSnapshot.id !== effectiveTcsId ||
        initialTcsSnapshot.name !== effectiveTcsName
      ) {
        changes.push({
          field: "TCS",
          previousValue: prevDisplay,
          newValue: currDisplay,
        });
      }
    }

    if (changes.length > 0) {
      setPendingChanges(changes);
      // Store TCS value to apply when user confirms (effective = what we're saving)
      setTcsValueToSaveOnConfirm(
        pendingTcsSelection !== undefined
          ? pendingTcsSelection
          : tcsTaxId
            ? {
                tcs_tax_id: tcsTaxId,
                tcs_tax_name: tcsTaxName ?? "",
                tcs_tax_percentage: tcsTaxPercentage ?? 0,
                tcs_nature_of_collection: undefined,
              }
            : null,
      );
      setIsReviewModalOpen(true);
    } else {
      setPendingTcsSelection(undefined);
      setTcsValueToSaveOnConfirm(undefined);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditData(data);
    setDiscountInputValue(currentDiscount.toFixed(2));
    setInitialTcsSnapshot(null);
    setPendingTcsSelection(undefined);
    setTcsValueToSaveOnConfirm(undefined);
    setIsEditing(false);
  };

  const handleConfirmChanges = () => {
    setData(editData);
    setInitialTcsSnapshot(null);
    setPendingTcsSelection(undefined);
    setIsEditing(false);
    setIsReviewModalOpen(false);
    setPendingChanges([]);

    // Update discount if it changed (round to 2 decimal places)
    const editingDiscountValue =
      parseFloat(parseFloat(discountInputValue).toFixed(2)) || 0;
    if (
      Math.abs(editingDiscountValue - currentDiscount) > 0.001 &&
      onDiscountChange
    ) {
      onDiscountChange(editingDiscountValue);
    }

    // Apply TCS only on confirm (parent state updated here)
    if (tcsValueToSaveOnConfirm !== undefined && onTcsChange) {
      onTcsChange(tcsValueToSaveOnConfirm);
    }
    setTcsValueToSaveOnConfirm(undefined);

    // Optional callback to parent component this will update the json !! to be used to send to DB later
    onSave?.(editData);
  };

  const handleCancelChanges = () => {
    setIsReviewModalOpen(false);
    setPendingChanges([]);
    // Keep editing mode open so user can continue editing
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const fetchTcsItems = useCallback(
    async (search: string, limit: number, offset: number) => {
      const res = await fetchTcsList(search, limit, offset);
      const items = res.data.tcs.map(mapTcsDTOToEntity);
      if (offset === 0) {
        return {
          items: [NO_TCS_ENTITY, ...items],
          hasMore: res.data.pagination.has_more,
        };
      }
      return { items, hasMore: res.data.pagination.has_more };
    },
    [],
  );

  const handleTcsSelect = useCallback((item: TcsEntity) => {
    if (item.tcs_id === "") {
      setPendingTcsSelection(null);
    } else {
      setPendingTcsSelection({
        tcs_tax_id: item.tcs_id,
        tcs_tax_name: item.tcs_tax_name,
        tcs_tax_percentage: item.tcs_tax_percentage,
        tcs_nature_of_collection: item.tcs_nature_of_collection,
      });
    }
  }, []);

  return (
    <div className="flex w-full flex-col items-start gap-3">
      <div className="flex w-full items-center justify-between">
        <span className="text-body-bold font-body-bold text-subtext-color">
          Amount Summary
        </span>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Button variant="brand-primary" size="small" onClick={handleSave}>
              <FeatherSave />
            </Button>
            <Button
              variant="neutral-tertiary"
              size="small"
              onClick={handleCancel}
            >
              <FeatherX />
            </Button>
          </div>
        ) : (
          !disabled && (
            <FeatherEdit
              className="text-body-bold font-body-bold text-subtext-color cursor-pointer hover:text-brand-600 transition-colors"
              onClick={handleEdit}
            />
          )
        )}
      </div>
      <div className="w-full items-start gap-4 grid grid-cols-3">
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            <span className="text-body font-body text-subtext-color">
              Subtotal
            </span>
          </div>
          <ReviewPendingInlineValue
            pendingReviewDiffActive={pendingReviewDiffActive}
            isEditing={isEditing}
            oldText={amountFieldDiffs?.sub_total?.oldText ?? ""}
            newText={amountFieldDiffs?.sub_total?.newText ?? ""}
          >
            <span className="text-body-bold font-body-bold text-default-font">
              {currencySymbol}
              {(
                rawSubtotal +
                (isEditing
                  ? parseFloat(discountInputValue) || 0
                  : currentDiscount)
              ).toFixed(2)}
            </span>
          </ReviewPendingInlineValue>
        </div>

        {isGstEnabled !== false && (
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <div className="flex items-center gap-2">
              <span className="text-body font-body text-subtext-color">
                GST Value
              </span>
            </div>
            <ReviewPendingInlineValue
              pendingReviewDiffActive={pendingReviewDiffActive}
              isEditing={isEditing}
              oldText={amountFieldDiffs?.tax_total?.oldText ?? ""}
              newText={amountFieldDiffs?.tax_total?.newText ?? ""}
            >
              <span className="text-body-bold font-body-bold text-default-font">
                {data.gstValue}
              </span>
            </ReviewPendingInlineValue>
          </div>
        )}

        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
          <div className="flex items-center gap-2">
            <span className="text-body font-body text-subtext-color">
              Discounts/Adjustments ({currencySymbol})
            </span>
          </div>
          {isEditing ? (
            <TextField
              label=""
              helpText="Use negative values for discounts, positive for additional charges"
            >
              <TextField.Input
                type="text"
                inputMode="decimal"
                value={discountInputValue}
                onChange={(e) => {
                  const inputValue = e.target.value;

                  // Allow empty, minus sign, or valid decimal numbers with max 2 decimal places
                  if (inputValue === "" || inputValue === "-") {
                    setDiscountInputValue(inputValue);
                    return;
                  }

                  // Check if it's a valid number format
                  // Allow: numbers, optional minus at start, optional single decimal point with max 2 digits after
                  const decimalRegex = /^-?\d*\.?\d{0,2}$/;
                  if (decimalRegex.test(inputValue)) {
                    setDiscountInputValue(inputValue);
                  }
                }}
                onBlur={() => {
                  // On blur, ensure we have a valid number formatted to 2 decimal places
                  const value = parseFloat(discountInputValue);
                  if (
                    isNaN(value) ||
                    discountInputValue === "" ||
                    discountInputValue === "-"
                  ) {
                    setDiscountInputValue("0.00");
                  } else {
                    setDiscountInputValue(value.toFixed(2));
                  }
                }}
                onKeyDown={handleKeyPress}
                placeholder="0.00 (negative for discount)"
                className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </TextField>
          ) : (
            <span
              className={`text-body-bold font-body-bold ${
                currentDiscount < 0
                  ? "text-success-600"
                  : currentDiscount > 0
                    ? "text-error-600"
                    : "text-default-font"
              }`}
            >
              {currentDiscount !== 0
                ? `${
                    currentDiscount > 0 ? "+" : ""
                  }${currencySymbol}${currentDiscount.toFixed(2)}`
                : "No adjustments"}
            </span>
          )}
        </div>
        {isGstEnabled && (
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-body font-body text-subtext-color">
                TCS
              </span>
              {tcsRecommendation != null &&
                !userHasChangedTcs &&
                !tcsDisabled && (
                  <SubframeCore.Tooltip.Provider>
                    <SubframeCore.Tooltip.Root>
                      <SubframeCore.Tooltip.Trigger asChild={true}>
                        <button
                          type="button"
                          className="inline-flex cursor-pointer items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 text-subtext-color hover:text-default-font"
                          aria-label="TCS AI recommendation"
                        >
                          <FeatherInfo className="h-4 w-4" />
                        </button>
                      </SubframeCore.Tooltip.Trigger>
                      <SubframeCore.Tooltip.Portal>
                        <SubframeCore.Tooltip.Content
                          side="top"
                          align="start"
                          sideOffset={4}
                          asChild={true}
                          className="max-w-[320px]"
                        >
                          <div className="flex min-w-[240px] max-w-[320px] flex-col items-start gap-2 rounded-md border border-solid border-neutral-border bg-white px-3 py-2 shadow-md">
                            {tcsRecommendation.match_status === "matched" && (
                              <>
                                <span className="text-caption-bold font-caption-bold text-default-font">
                                  AI Suggestion
                                </span>
                                {tcsRecommendation.matched_tax_name != null &&
                                  tcsRecommendation.tcs_rate != null && (
                                    <span className="text-caption font-caption text-default-font">
                                      {tcsRecommendation.matched_tax_name} (
                                      {tcsRecommendation.tcs_rate}%)
                                    </span>
                                  )}
                                <span className="text-caption font-caption text-subtext-color">
                                  Confidence:{" "}
                                  {Math.round(
                                    tcsRecommendation.confidence * 100
                                  )}
                                  %
                                </span>
                                {tcsRecommendation.ai_reasoning && (
                                  <p className="text-caption font-caption text-default-font whitespace-pre-wrap">
                                    {tcsRecommendation.ai_reasoning}
                                  </p>
                                )}
                              </>
                            )}
                            {tcsRecommendation.match_status === "unresolved" && (
                              <>
                                <span className="text-caption-bold font-caption-bold text-default-font">
                                  AI could not confidently match TCS
                                </span>
                                <span className="text-caption font-caption text-subtext-color">
                                  Confidence:{" "}
                                  {Math.round(
                                    tcsRecommendation.confidence * 100
                                  )}
                                  %
                                </span>
                                {tcsRecommendation.ai_reasoning && (
                                  <p className="text-caption font-caption text-default-font whitespace-pre-wrap">
                                    {tcsRecommendation.ai_reasoning}
                                  </p>
                                )}
                              </>
                            )}
                            {tcsRecommendation.match_status === "no_match" && (
                              <>
                                <span className="text-caption-bold font-caption-bold text-default-font">
                                  No TCS match found
                                </span>
                                {tcsRecommendation.ai_reasoning && (
                                  <p className="text-caption font-caption text-default-font whitespace-pre-wrap">
                                    {tcsRecommendation.ai_reasoning}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        </SubframeCore.Tooltip.Content>
                      </SubframeCore.Tooltip.Portal>
                    </SubframeCore.Tooltip.Root>
                  </SubframeCore.Tooltip.Provider>
                )}
            </div>
            {tcsDisabled ? (
              <div className="flex flex-col items-start gap-0.5">
                <ReviewPendingInlineValue
                  pendingReviewDiffActive={pendingReviewDiffActive}
                  isEditing={isEditing}
                  oldText={amountFieldDiffs?.tcs_tax_name?.oldText ?? ""}
                  newText={amountFieldDiffs?.tcs_tax_name?.newText ?? ""}
                >
                  <span className="text-body-bold font-body-bold text-default-font">
                    {tcsTaxName && tcsTaxId ? tcsTaxName : "No TCS"}
                  </span>
                </ReviewPendingInlineValue>
                <span className="text-caption font-caption text-subtext-color">
                  {tcsDisabledMessage}
                </span>
              </div>
            ) : isEditing && onTcsChange ? (
              <div className="w-full max-w-[220px]">
                <SearchableDropdown<TcsEntity>
                  fetchItems={fetchTcsItems}
                  initialLimit={10}
                  maxHeight="max-h-60"
                  className="w-full"
                  dropdownClassName="min-w-[200px]"
                  trigger={
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      iconRight={<FeatherChevronDown />}
                      className="w-full justify-between min-h-10 h-10 py-2"
                    >
                      <span
                        className="text-left"
                        title={
                          effectiveTcsName && effectiveTcsId
                            ? effectiveTcsName
                            : "No TCS"
                        }
                      >
                        {truncateText(
                          effectiveTcsName && effectiveTcsId
                            ? effectiveTcsName
                            : "No TCS",
                          24,
                        )}
                      </span>
                    </Button>
                  }
                  placeholder="Select TCS"
                  getSearchableText={(item) => item.tcs_tax_name}
                  renderItem={(item) =>
                    item.tcs_id === ""
                      ? item.tcs_tax_name
                      : `${item.tcs_tax_name} (${item.tcs_tax_percentage}%)`
                  }
                  getItemKey={(item) => item.tcs_id}
                  onSelect={handleTcsSelect}
                  selectedKey={effectiveTcsId}
                  disabled={disabled}
                />
              </div>
            ) : (
              <div className="flex w-full min-w-0 flex-col items-start gap-1">
                <ReviewPendingInlineValue
                  pendingReviewDiffActive={pendingReviewDiffActive}
                  isEditing={isEditing}
                  oldText={amountFieldDiffs?.tcs_tax_name?.oldText ?? ""}
                  newText={amountFieldDiffs?.tcs_tax_name?.newText ?? ""}
                >
                  <span className="text-body-bold font-body-bold text-default-font">
                    {tcsTaxName && tcsTaxId ? tcsTaxName : "No TCS"}
                  </span>
                </ReviewPendingInlineValue>
                <ReviewPendingInlineValue
                  pendingReviewDiffActive={pendingReviewDiffActive}
                  isEditing={isEditing}
                  oldText={amountFieldDiffs?.tcs_tax_percentage?.oldText ?? ""}
                  newText={amountFieldDiffs?.tcs_tax_percentage?.newText ?? ""}
                >
                  <span className="text-caption font-caption text-subtext-color">
                    {tcsTaxPercentage != null && tcsTaxPercentage !== undefined
                      ? `${tcsTaxPercentage}%`
                      : "\u00a0"}
                  </span>
                </ReviewPendingInlineValue>
              </div>
            )}
          </div>
        )}

        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1 border-t border-solid border-neutral-border pt-4 col-span-3">
          <div className="flex items-center gap-2">
            <span className="text-body-bold font-body-bold text-default-font">
              Total Amount
            </span>
          </div>
          <ReviewPendingInlineValue
            pendingReviewDiffActive={pendingReviewDiffActive}
            isEditing={isEditing}
            oldText={amountFieldDiffs?.total?.oldText ?? ""}
            newText={amountFieldDiffs?.total?.newText ?? ""}
            oldValueClassName="line-through opacity-70 text-heading-3 font-heading-3 text-brand-600"
            newValueClassName="text-heading-3 font-heading-3 text-brand-600"
          >
            <span className="text-heading-3 font-heading-3 text-brand-600">
              {currencySymbol}
              {(() => {
                const subtotalWithDiscount =
                  rawSubtotal +
                  (isEditing
                    ? parseFloat(discountInputValue) || 0
                    : currentDiscount);
                const gstValue = isGstEnabled ? calculatedGst : 0;
                const baseForTcs = subtotalWithDiscount + gstValue;
                const tcsAmount =
                  isGstEnabled &&
                  effectiveTcsId &&
                  effectiveTcsPct != null &&
                  effectiveTcsPct > 0
                    ? baseForTcs * (effectiveTcsPct / 100)
                    : 0;
                return (subtotalWithDiscount + gstValue + tcsAmount).toFixed(
                  2,
                );
              })()}
            </span>
          </ReviewPendingInlineValue>
        </div>
      </div>

      {/* Review Changes Modal */}
      <ReviewChangesModal
        open={isReviewModalOpen}
        onOpenChange={setIsReviewModalOpen}
        changes={pendingChanges}
        onConfirm={handleConfirmChanges}
        onCancel={handleCancelChanges}
        title="Review Amount Summary Changes"
        description="These changes will affect the invoice calculations and tax implications. Please review carefully before proceeding."
        contentClassName="min-w-[32rem] w-full max-w-xl px-6 py-8"
      />
    </div>
  );
};

export default AmountSummaryEditableField;
